/**
 * Design tokens for SmartMixin webapp
 * Centralized design system values for consistent UI
 */

// Weather-specific colors
export const weatherColors = {
  hot: '#f13a59',
  warm: '#FF9800',
  mild: '#FFB800',
  cool: '#33B5E5',
  cold: '#2196F3',
  rain: '#5C9CE6',
  sun: '#FFB800',
  wind: '#8BC34A',
  snow: '#B3E5FC',
  storm: '#7C4DFF',
  fog: '#90A4AE',
  cloud: '#78909C',
};

// Status colors for station connectivity
export const statusColors = {
  online: '#4CAF50',
  stale: '#FF9800',
  offline: '#F44336',
  syncing: '#2196F3',
};

// Brand colors
export const brandColors = {
  primary: '#33B5E5',
  secondary: '#fb5f26',
};

// Border radius scale
export const borderRadius = {
  xs: 2,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  round: 9999,
} as const;

// Shadow definitions
export const shadows = {
  card: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.06)',
  cardHover: '0px 4px 6px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.06)',
  elevated: '0px 10px 15px rgba(0, 0, 0, 0.1), 0px 4px 6px rgba(0, 0, 0, 0.05)',
  modal: '0px 20px 25px rgba(0, 0, 0, 0.15), 0px 10px 10px rgba(0, 0, 0, 0.05)',
  focus: '0px 0px 0px 3px rgba(51, 181, 229, 0.3)',
};

// Dark mode shadows
export const shadowsDark = {
  card: '0px 1px 3px rgba(0, 0, 0, 0.3), 0px 1px 2px rgba(0, 0, 0, 0.25)',
  cardHover: '0px 4px 6px rgba(0, 0, 0, 0.35), 0px 2px 4px rgba(0, 0, 0, 0.25)',
  elevated: '0px 10px 15px rgba(0, 0, 0, 0.4), 0px 4px 6px rgba(0, 0, 0, 0.3)',
  modal: '0px 20px 25px rgba(0, 0, 0, 0.5), 0px 10px 10px rgba(0, 0, 0, 0.35)',
  focus: '0px 0px 0px 3px rgba(51, 181, 229, 0.4)',
};

// Spacing scale (based on 8px grid)
export const spacing = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;

// Typography scale
export const typography = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontFamilyMono: "'JetBrains Mono', 'Fira Code', Consolas, monospace",

  // Font weights
  weights: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // Line heights
  lineHeights: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.625,
  },

  // Font sizes
  sizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    md: '1rem',       // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
  },
};

// Navigation dimensions
export const navigation = {
  railWidth: 72,
  railExpandedWidth: 280,
  railWidthMobile: 56,
  bottomNavHeight: 56,
  appBarHeight: 64,
  appBarHeightMobile: 56,
};

// Breakpoints
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

// Z-index scale
export const zIndex = {
  drawer: 1200,
  appBar: 1300,
  modal: 1400,
  tooltip: 1500,
};

// Transitions
export const transitions = {
  fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
  normal: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
  spring: '400ms cubic-bezier(0.175, 0.885, 0.32, 1.275)',
};

// Temperature color thresholds and colors
export const temperatureScale = {
  freezing: { threshold: 0, color: '#2196F3' },
  cold: { threshold: 10, color: '#33B5E5' },
  cool: { threshold: 15, color: '#4CAF50' },
  mild: { threshold: 20, color: '#8BC34A' },
  warm: { threshold: 25, color: '#FFB800' },
  hot: { threshold: 30, color: '#FF9800' },
  veryHot: { threshold: 35, color: '#f13a59' },
};

/**
 * Get color for temperature value (in Celsius)
 */
export const getTemperatureColor = (tempCelsius: number): string => {
  if (tempCelsius < temperatureScale.freezing.threshold) return temperatureScale.freezing.color;
  if (tempCelsius < temperatureScale.cold.threshold) return temperatureScale.cold.color;
  if (tempCelsius < temperatureScale.cool.threshold) return temperatureScale.cool.color;
  if (tempCelsius < temperatureScale.mild.threshold) return temperatureScale.mild.color;
  if (tempCelsius < temperatureScale.warm.threshold) return temperatureScale.warm.color;
  if (tempCelsius < temperatureScale.hot.threshold) return temperatureScale.hot.color;
  return temperatureScale.veryHot.color;
};

/**
 * Get status color based on time since last update
 */
export const getStationStatusColor = (minutesSinceUpdate: number): string => {
  if (minutesSinceUpdate <= 30) return statusColors.online;
  if (minutesSinceUpdate <= 240) return statusColors.stale;
  return statusColors.offline;
};

/**
 * Get status label based on time since last update
 */
export const getStationStatusLabel = (minutesSinceUpdate: number): 'online' | 'stale' | 'offline' => {
  if (minutesSinceUpdate <= 30) return 'online';
  if (minutesSinceUpdate <= 240) return 'stale';
  return 'offline';
};
